import { useId, useRef, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { BotaoDoRaro } from '@/componentes/LinhaDoPreenchido';
import { formatarDataHora } from '@/lib/formato';

import type { DetalheDaOrdem } from './api';
import { abrirAnexo, explicarFalha, useAnexar } from './consultas';
import { ROTULO_DA_ETAPA } from './ordem';

/**
 * Foto e anexo da O.S. (migração 0026) — a evidência em arquivo que o v6 pede.
 *
 * O anexo não se edita nem se apaga: é prova de como a máquina estava e de como
 * ficou. Cada um leva a etapa em que a O.S. estava quando entrou, e é ela que
 * separa o "antes" do "depois" sem ninguém precisar dizer.
 */

/** O mesmo teto do servidor. Conferir aqui poupa subir 200 MB para ouvir "não". */
const TAMANHO_MAXIMO_BYTES = 100 * 1024 * 1024;

type Recusa = { readonly erro: string; readonly comoResolver: string } | null;
type CampoDoAnexo = 'arquivo' | 'descricao';

export function AnexosDaOrdem({ detalhe, podeAnexar }: { readonly detalhe: DetalheDaOrdem; readonly podeAnexar: boolean }) {
  const base = useId();
  const anexar = useAnexar();
  const campoDoArquivo = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [descricao, setDescricao] = useState('');
  const [erros, setErros] = useState<Readonly<Record<CampoDoAnexo, string | null>>>({ arquivo: null, descricao: null });
  const [recusa, setRecusa] = useState<Recusa>(null);
  const [anexando, setAnexando] = useState(false);

  /* O erro vai abaixo do campo que ele descreve (p.16). O do arquivo confere
     na escolha — o "sair" de um campo de arquivo é abrir a janela do sistema,
     e cobrar o arquivo nesse instante seria cobrar antes de a pessoa escolher. */
  function checar(campo: CampoDoAnexo, escolhido: File | null = arquivo): string | null {
    if (campo === 'descricao') {
      return descricao.trim() === '' ? 'Diga o que o arquivo mostra, por exemplo "cilindro antes do reparo".' : null;
    }
    if (escolhido === null) return 'Escolha a foto ou o documento.';
    return escolhido.size > TAMANHO_MAXIMO_BYTES ? 'O arquivo passa de 100 MB. Reduza a foto ou divida o documento.' : null;
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = { arquivo: checar('arquivo'), descricao: checar('descricao') };
    setErros(achados);
    if (achados.arquivo || achados.descricao || arquivo === null) return;
    setRecusa(null);
    anexar.mutate(
      { id: detalhe.ordem.id, arquivo, descricao: descricao.trim() },
      {
        onSuccess: () => {
          setArquivo(null);
          setDescricao('');
          if (campoDoArquivo.current) campoDoArquivo.current.value = '';
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <section className="flex flex-col gap-4 border-t border-borda-sutil pt-4">
      <div className="flex flex-col gap-1">
        <h3 className="txt-subtitulo text-tinta">Fotos e anexos</h3>
        <p className="txt-apoio text-tinta-secundaria">
          Evidência não se apaga nem se edita. Faltou mostrar algo, anexe outro arquivo.
        </p>
      </div>

      {detalhe.anexos.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum anexo nesta O.S."
          descricao={podeAnexar ? 'Anexe a foto do defeito, a do serviço pronto ou o laudo do terceiro.' : 'Ninguém anexou foto ou documento a esta O.S.'}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {detalhe.anexos.map((anexo) => (
            <li key={anexo.id} className="flex items-baseline justify-between gap-3 border-b border-borda-sutil pb-2">
              <span className="flex min-w-0 flex-col">
                <span className="txt-corpo text-tinta">{anexo.descricao}</span>
                <span className="txt-apoio text-tinta-apagada">
                  {`${ROTULO_DA_ETAPA[anexo.etapa]} · ${formatarDataHora(new Date(anexo.enviadoEm))} · ${anexo.enviadoPor ?? 'sem nome'}`}
                </span>
              </span>
              <Botao
                nivel="terciario"
                onClick={() => {
                  abrirAnexo(anexo.id).catch((falha: unknown) => {
                    setRecusa(explicarFalha(falha));
                  });
                }}
              >
                Abrir anexo
              </Botao>
            </li>
          ))}
        </ul>
      )}

      {/* ANEXAR É O RARO DA ABA (31/10/2026): o botão revela o formulário, e
          a lista do que já foi anexado continua à vista. */}
      {podeAnexar ? (
        <BotaoDoRaro
          aberto={anexando}
          aoAlternar={() => {
            setAnexando((aberto) => !aberto);
          }}
          rotulo="Anexar foto ou documento"
          rotuloAberto="Fechar o anexo"
          idDoConteudo={`${base}-anexar`}
        />
      ) : null}
      {podeAnexar && anexando ? (
        <form id={`${base}-anexar`} onSubmit={enviar} noValidate className="flex flex-col gap-4">
          <div className="grade-do-painel">
          <Campo id={`${base}-arquivo`} rotulo="Foto ou documento" erro={erros.arquivo}>
            <input
              id={`${base}-arquivo`}
              ref={campoDoArquivo}
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              className="controle w-full"
              onChange={(e) => {
                const escolhido = e.target.files?.[0] ?? null;
                setArquivo(escolhido);
                setErros((atual) => ({ ...atual, arquivo: escolhido === null ? null : checar('arquivo', escolhido) }));
              }}
              {...(erros.arquivo ? { 'aria-invalid': true, 'aria-describedby': `${base}-arquivo-erro` } : {})}
            />
          </Campo>
          <Campo id={`${base}-descricao`} rotulo="O que o arquivo mostra" erro={erros.descricao}>
            <input
              id={`${base}-descricao`}
              type="text"
              autoComplete="off"
              maxLength={300}
              className="controle w-full"
              placeholder="Cilindro do braço antes do reparo"
              value={descricao}
              onChange={(e) => {
                setDescricao(e.target.value);
                setErros((atual) => ({ ...atual, descricao: null }));
              }}
              onBlur={() => {
                setErros((atual) => ({ ...atual, descricao: checar('descricao') }));
              }}
              {...(erros.descricao ? { 'aria-invalid': true, 'aria-describedby': `${base}-descricao-erro` } : {})}
            />
          </Campo>
          </div>
          <div className="flex justify-end">
            <Botao nivel="secundario" type="submit" disabled={anexar.isPending}>
              {anexar.isPending ? 'Enviando…' : 'Anexar arquivo'}
            </Botao>
          </div>
        </form>
      ) : null}

      {recusa !== null ? (
        <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
          <span className="txt-corpo text-tinta">{recusa.erro}</span>
          <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
        </div>
      ) : null}
    </section>
  );
}
